import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { organization, user } from '../../auth'
import { Challenge, LessonModule } from '../../content'
import { Course } from '../course'
import { LearnEnrollment } from '../learn-enrollment'

// =============================================================================
// LESSON ENROLLMENT METRICS - Lookup table (refreshed nightly; stale rows overlaid live)
// Per-enrollment snapshot for the teacher dashboard "Belajar" roster table.
// Like LeaderboardEntry, but covers ALL enrollment types (course + challenge +
// practice/lessonModule), so it is NOT used for XP ranking — practice rows have
// no course/challenge and must never pollute the leaderboard RANK().
//
// GRAIN: exactly ONE row PER ENROLLMENT. Unique on learnEnrollmentId is the
//   upsert conflict target.
// FORMULA: `computeLessonEnrollmentMetrics` (packages/trigger/src/metrics/).
// POPULATED BY: `calculateLessonEnrollmentMetrics` (stale guard: no row, or
//   learn_enrollment.updated_at > calculated_at), called in-process by the nightly
//   cron / rollup-lookup-tables, core / rollup-users and reset-org-lookup-tables.
// READ BY: metrics.getLessonTrend (grouped by userId), with `lessonEnrollmentMetricsToday`
//   recomputing every stale / changed-today / missing enrollment live over the stored rows.
// =============================================================================

export const LessonEnrollmentMetrics = pgTable(
  'lesson_enrollment_metrics',
  {
    id: uuid().defaultRandom().primaryKey(),
    learnEnrollmentId: uuid()
      .references(() => LearnEnrollment.id, { onDelete: 'cascade' })
      .notNull(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    organizationId: text()
      .references(() => organization.id, { onDelete: 'cascade' })
      .notNull(),
    // Exactly ONE of courseId / challengeId / lessonModuleId is set (mirrors LearnEnrollment).
    courseId: uuid().references(() => Course.id, { onDelete: 'set null' }),
    challengeId: uuid().references(() => Challenge.id, { onDelete: 'set null' }),
    lessonModuleId: uuid().references(() => LessonModule.id, { onDelete: 'set null' }),
    // LESSON = Tugas (course), CHALLENGE = Tantangan, PRACTICE = Mandiri/Latihan (lessonModule).
    type: text({ enum: ['LESSON', 'CHALLENGE', 'PRACTICE'] }).notNull(),

    totalXp: numericAsNumber().notNull().default(0), // RAW sum(EarnedXp.xpEarned), no /100
    totalXpPossible: numericAsNumber().notNull().default(0), // RAW sum(LessonToSkillWeight.value), no /100
    gradedXp: numericAsNumber().notNull().default(0), // round(totalXp/totalXpPossible*100), 0-100
    progress: numericAsNumber().notNull().default(0), // round(lessonsCompletedCount/totalLessonsCount*100), 0-100
    lessonsCompletedCount: integer().notNull().default(0),
    totalLessonsCount: integer().notNull().default(0),
    isCompleted: boolean().notNull().default(false),
    completedAt: timestamp({ mode: 'date', withTimezone: true }),
    lastActivityAt: timestamp({ mode: 'date', withTimezone: true }),

    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    unique('uq_lesson_enrollment_metrics_enrollment').on(t.learnEnrollmentId),
    index('idx_lesson_enrollment_metrics_user').on(t.userId),
    index('idx_lesson_enrollment_metrics_org').on(t.organizationId),
    index('idx_lesson_enrollment_metrics_org_user').on(t.organizationId, t.userId),
  ],
)

export const lessonEnrollmentMetricsRelations = relations(LessonEnrollmentMetrics, ({ one }) => ({
  learnEnrollment: one(LearnEnrollment, {
    fields: [LessonEnrollmentMetrics.learnEnrollmentId],
    references: [LearnEnrollment.id],
  }),
  user: one(user, {
    fields: [LessonEnrollmentMetrics.userId],
    references: [user.id],
  }),
  organization: one(organization, {
    fields: [LessonEnrollmentMetrics.organizationId],
    references: [organization.id],
  }),
  course: one(Course, {
    fields: [LessonEnrollmentMetrics.courseId],
    references: [Course.id],
  }),
  challenge: one(Challenge, {
    fields: [LessonEnrollmentMetrics.challengeId],
    references: [Challenge.id],
  }),
  lessonModule: one(LessonModule, {
    fields: [LessonEnrollmentMetrics.lessonModuleId],
    references: [LessonModule.id],
  }),
}))

export const InsertLessonEnrollmentMetricsSchema = createInsertSchema(LessonEnrollmentMetrics).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateLessonEnrollmentMetricsSchema = createUpdateSchema(LessonEnrollmentMetrics).partial().omit({
  id: true,
  learnEnrollmentId: true,
  userId: true,
  organizationId: true,
  courseId: true,
  challengeId: true,
  lessonModuleId: true,
  type: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectLessonEnrollmentMetricsSchema = createSelectSchema(LessonEnrollmentMetrics)

export type InsertLessonEnrollmentMetrics = typeof LessonEnrollmentMetrics.$inferInsert
export type UpdateLessonEnrollmentMetrics = z.infer<typeof UpdateLessonEnrollmentMetricsSchema>
export type SelectLessonEnrollmentMetrics = typeof LessonEnrollmentMetrics.$inferSelect
